import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ease } from "@/lib/motion";
import { LucideIcon, ArrowRight, Database, Cpu, BarChart3, Target } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export interface FlowNode {
  id: string;
  label: string;
  description?: string;
  icon?: LucideIcon;
  type: "source" | "transform" | "decision" | "output";
}

export interface FlowConnection {
  from: string;
  to: string;
  label?: string;
}

export interface DataFlowchartProps {
  title?: string;
  label?: string;
  description?: string;
  nodes: FlowNode[];
  connections: FlowConnection[];
}

const nodeTypeConfig = {
  source: {
    color: "hsl(220, 25%, 45%)",
    bgColor: "hsl(220, 25%, 45%, 0.1)",
    borderColor: "hsl(220, 25%, 45%, 0.3)",
    label: "Data Sources",
  },
  transform: {
    color: "hsl(216, 99%, 33%)",
    bgColor: "hsl(216, 99%, 33%, 0.1)",
    borderColor: "hsl(216, 99%, 33%, 0.3)",
    label: "Transformation",
  },
  decision: {
    color: "hsl(169, 66%, 42%)",
    bgColor: "hsl(169, 66%, 42%, 0.1)",
    borderColor: "hsl(169, 66%, 42%, 0.3)",
    label: "Decision Points",
  },
  output: {
    color: "hsl(275, 50%, 52%)",
    bgColor: "hsl(275, 50%, 52%, 0.1)",
    borderColor: "hsl(275, 50%, 52%, 0.3)",
    label: "Business Outcomes",
  },
};

const defaultIcon: Record<FlowNode["type"], LucideIcon> = {
  source: Database,
  transform: Cpu,
  decision: BarChart3,
  output: Target,
};

const detailContentVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] as const } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

const DataFlowchart = ({
  title = "How data becomes decisions.",
  label = "Data Flow",
  description,
  nodes,
  connections,
}: DataFlowchartProps) => {
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [mobileActiveNode, setMobileActiveNode] = useState<string>(nodes[0]?.id ?? "");
  const isMobile = useIsMobile();

  // Group nodes by type
  const groupedNodes = {
    source: nodes.filter((n) => n.type === "source"),
    transform: nodes.filter((n) => n.type === "transform"),
    decision: nodes.filter((n) => n.type === "decision"),
    output: nodes.filter((n) => n.type === "output"),
  };

  const currentDesktopNode = activeNode ? nodes.find((n) => n.id === activeNode) : null;
  const currentMobileNode = nodes.find((n) => n.id === mobileActiveNode) || nodes[0];

  // Get connections for a given node
  const getConnections = useCallback((nodeId: string | null) => {
    if (!nodeId) return { connections: [], connectedIds: [] };
    const conns = connections.filter((c) => c.from === nodeId || c.to === nodeId);
    const ids = conns.map((c) => (c.from === nodeId ? c.to : c.from));
    return { connections: conns, connectedIds: ids };
  }, [connections]);

  const { connectedIds: desktopConnectedIds } = getConnections(activeNode);
  const { connections: mobileConns } = getConnections(mobileActiveNode);

  // Render detail content (shared between desktop and mobile)
  const renderDetailContent = (node: FlowNode, relatedConnections: FlowConnection[], hoveredId: string | null) => {
    const config = nodeTypeConfig[node.type];
    const Icon = node.icon || defaultIcon[node.type];

    return (
      <div
        className="p-6 rounded-xl border-2"
        style={{
          borderColor: config.color,
          backgroundColor: config.bgColor,
        }}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${config.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: config.color }} />
          </div>
          <div>
            <h4 className="font-semibold text-foreground">{node.label}</h4>
            <p className="text-sm text-muted-foreground">{node.description}</p>
          </div>

          {relatedConnections.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Connected to:</span>
              <div className="flex gap-1 flex-wrap">
                {relatedConnections.map((conn) => {
                  const targetId = conn.from === hoveredId ? conn.to : conn.from;
                  const targetNode = nodes.find((n) => n.id === targetId);
                  if (!targetNode) return null;
                  return (
                    <span
                      key={targetId}
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{
                        backgroundColor: nodeTypeConfig[targetNode.type].bgColor,
                        color: nodeTypeConfig[targetNode.type].color,
                      }}
                    >
                      {targetNode.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="py-12 md:py-16 lg:py-24 border-t border-border bg-muted/10">
      <div className="container-wide">
        {/* Header */}
        <motion.div
          className="max-w-2xl mb-10 md:mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, ease }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
            {label}
          </span>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-3">
            {title}
          </h2>
          {description && (
            <p className="text-muted-foreground">{description}</p>
          )}
        </motion.div>

        {/* Desktop Flowchart */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Flow Stages */}
            <div className="grid grid-cols-4 gap-6">
              {(["source", "transform", "decision", "output"] as const).map((type, stageIndex) => {
                const config = nodeTypeConfig[type];
                const stageNodes = groupedNodes[type];

                return (
                  <motion.div
                    key={type}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * stageIndex, duration: 0.5, ease }}
                  >
                    {/* Stage Header */}
                    <div className="mb-4">
                      <div
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: config.bgColor,
                          color: config.color,
                        }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: config.color }} />
                        {config.label}
                      </div>
                    </div>

                    {/* Nodes */}
                    <div className="space-y-3">
                      {stageNodes.map((node, nodeIndex) => {
                        const Icon = node.icon || defaultIcon[node.type];
                        const isActive = activeNode === node.id;
                        const isConnected = desktopConnectedIds.includes(node.id);
                        const hasActiveNode = activeNode !== null;

                        return (
                          <motion.div
                            key={node.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 * stageIndex + 0.05 * nodeIndex, duration: 0.3 }}
                            className={`
                              relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-300
                              ${isActive
                                ? "shadow-lg scale-[1.02]"
                                : hasActiveNode && !isConnected
                                  ? "opacity-40"
                                  : "hover:shadow-md"
                              }
                            `}
                            style={{
                              backgroundColor: isActive ? config.bgColor : "hsl(var(--background))",
                              borderColor: isActive || isConnected ? config.color : config.borderColor,
                            }}
                            onMouseEnter={() => setActiveNode(node.id)}
                            onMouseLeave={() => setActiveNode(null)}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: config.bgColor }}
                              >
                                <Icon className="w-4 h-4" style={{ color: config.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-sm text-foreground">
                                  {node.label}
                                </h4>
                                {node.description && (
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    {node.description}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Connection indicator */}
                            {isConnected && !isActive && (
                              <motion.div
                                className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: config.color }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.2 }}
                              >
                                <ArrowRight className="w-2.5 h-2.5 text-white" />
                              </motion.div>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>

                    {/* Arrow to next stage */}
                    {stageIndex < 3 && (
                      <div className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center"
                        style={{
                          left: `calc(${(stageIndex + 1) * 25}% - 12px)`,
                        }}
                      >
                        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                          <ArrowRight className="w-3 h-3 text-muted-foreground" />
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Connection Lines SVG */}
            <div className="absolute inset-0 pointer-events-none">
              <svg className="w-full h-full" style={{ position: "absolute", top: 0, left: 0 }}>
                <defs>
                  <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="hsl(220, 25%, 45%)" stopOpacity="0.3" />
                    <stop offset="33%" stopColor="hsl(216, 99%, 33%)" stopOpacity="0.3" />
                    <stop offset="66%" stopColor="hsl(169, 66%, 42%)" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="hsl(275, 50%, 52%)" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>

          {/* Fixed-height detail container — always present, never collapses */}
          <div className="mt-8 min-h-[90px]">
            <AnimatePresence mode="wait">
              {currentDesktopNode && (
                <motion.div
                  key={currentDesktopNode.id}
                  variants={detailContentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {renderDetailContent(
                    currentDesktopNode,
                    connections.filter((c) => c.from === activeNode || c.to === activeNode),
                    activeNode
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile/Tablet — Click-based tab view */}
        <div className="lg:hidden">
          {/* Node tabs — scrollable row */}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-1 px-1 scrollbar-hide">
            {nodes.map((node) => {
              const config = nodeTypeConfig[node.type];
              const isActive = mobileActiveNode === node.id;
              const Icon = node.icon || defaultIcon[node.type];

              return (
                <button
                  key={node.id}
                  onClick={() => setMobileActiveNode(node.id)}
                  className={`
                    flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-left whitespace-nowrap flex-shrink-0 transition-all duration-200
                    ${isActive ? "shadow-md" : "opacity-60"}
                  `}
                  style={{
                    backgroundColor: isActive ? config.bgColor : "transparent",
                    borderColor: isActive ? config.color : config.borderColor,
                  }}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: config.bgColor }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: config.color }} />
                  </div>
                  <span className="text-xs font-medium text-foreground">{node.label}</span>
                </button>
              );
            })}
          </div>

          {/* Fixed detail container — always present */}
          <div className="min-h-[120px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentMobileNode.id}
                variants={detailContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {renderDetailContent(currentMobileNode, mobileConns, mobileActiveNode)}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Flow indicator */}
          <div className="flex justify-center py-4">
            <div className="flex items-center gap-2">
              {(["source", "transform", "decision", "output"] as const).map((type, index) => (
                <div key={type} className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: nodeTypeConfig[type].color }}
                  />
                  {index < 3 && (
                    <ArrowRight className="w-4 h-4 text-muted-foreground mx-1" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DataFlowchart;
